import { ApplicationsEnum, StoreWrapperInterface, STORE_WRAPPER_TOKEN } from '@actassa/api';
import { Inject, Injectable } from '@angular/core';
import { Actions, ofActionDispatched } from '@ngxs/store';
import { isEqual } from 'lodash-es';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { Observable, merge, BehaviorSubject, NEVER, Subscription, combineLatest, concat } from 'rxjs';
import { catchError, delay, distinctUntilChanged, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { LoadTemplatesSuccess } from '../+state/actions/load-templates-success';
import { IDB_DATA_COLLECTION_TEMPLATES_STORE, IDB_INDEX_PROVIDER_ID } from '../constants/idb.config';
import { TemplateInterface } from '../interfaces/template.interface';
import { DataCollectionType } from '../types/inspection-type';

@Injectable({
    providedIn: 'root',
})
export class DataCollectionTemplatesStoreService {
    private readonly dataCollectionTemplatesActivator$ = new BehaviorSubject(null);
    private subscriptions: Subscription;

    constructor(
        @Inject(STORE_WRAPPER_TOKEN) private readonly mainStore: StoreWrapperInterface,
        private readonly actions$: Actions,
        private readonly dbService: NgxIndexedDBService,
    ) {
        this.isActionSubscriptionEnabled$
            .pipe(
                distinctUntilChanged(),
                tap((isAuthenticated: boolean) => {
                    if (isAuthenticated) {
                        const initSubscription = this.init$().subscribe();

                        this.subscriptions = new Subscription();
                        this.subscriptions.add(initSubscription);

                        return;
                    }

                    this.subscriptions?.unsubscribe();
                }),
            )
            .subscribe();
    }

    private init$(): Observable<unknown> {
        return merge(
            this.actions$.pipe(
                ofActionDispatched(LoadTemplatesSuccess),
                distinctUntilChanged(isEqual),
                withLatestFrom(this.mainStore.app$, this.mainStore.providerId$),
                switchMap(([{ templates }, app, providerId]: [LoadTemplatesSuccess, ApplicationsEnum, string]) =>
                    this.dbService.getAllByIndex(
                        IDB_DATA_COLLECTION_TEMPLATES_STORE, IDB_INDEX_PROVIDER_ID, IDBKeyRange.only(providerId),
                    )
                        .pipe(
                            switchMap((localTemplates: Array<TemplateInterface>) => {
                                const currentApplicationLocalTemplates = this.getCurrentApplicationTemplates(localTemplates, app);
                                const currentApplicationLocalTemplatesIds = this.getTemplatesIds(currentApplicationLocalTemplates);

                                return this.dbService.bulkDelete(IDB_DATA_COLLECTION_TEMPLATES_STORE, currentApplicationLocalTemplatesIds);
                            }),
                            switchMap(() => concat(...templates.map(template => this.dbService.add(IDB_DATA_COLLECTION_TEMPLATES_STORE, template)))),
                        )),
            ),
        ).pipe(
            delay(0),
            tap(() => this.activate()),
            catchError((error) => {
                this.mainStore.showToast(error.message);

                return NEVER;
            }),
        );
    }

    public getDataCollectionTemplates$(types: Array<DataCollectionType>): Observable<Array<TemplateInterface>> {
        return this.dataCollectionTemplatesActivator$
            .pipe(
                withLatestFrom(this.mainStore.providerId$),
                switchMap(([, providerId]: [null, string]) => this.dbService.getAllByIndex<TemplateInterface>(
                    IDB_DATA_COLLECTION_TEMPLATES_STORE, IDB_INDEX_PROVIDER_ID, IDBKeyRange.only(providerId),
                )),
                withLatestFrom(this.mainStore.app$),
                map(([templates, app]: [Array<TemplateInterface>, ApplicationsEnum]) =>
                    this.getCurrentApplicationTemplates(templates, app)),
                map((templates: Array<TemplateInterface>) => this.filterTemplatesByType(templates, types)),
            );
    }

    public activate(): void {
        this.dataCollectionTemplatesActivator$.next(null);
    }

    public getDataCollectionTemplate$(id: string): Observable<TemplateInterface> {
        return this.dbService.getByKey<TemplateInterface>(IDB_DATA_COLLECTION_TEMPLATES_STORE, id);
    }

    private getTemplatesIds(templates: Array<TemplateInterface>): Array<string> {
        return templates.map(({ id }: TemplateInterface) => id);
    }

    private getCurrentApplicationTemplates(templates: Array<TemplateInterface>, app: ApplicationsEnum): Array<TemplateInterface> {
        return templates.filter(({ application }: TemplateInterface) => application === app);
    }

    private filterTemplatesByType(templates: Array<TemplateInterface>, types: Array<DataCollectionType>): Array<TemplateInterface> {
        return templates.filter(({ type }: TemplateInterface) => types.includes(type));
    }

    private get isActionSubscriptionEnabled$(): Observable<boolean> {
        const { isAuthenticated$, app$ } = this.mainStore;
        const enabledRoles$ = app$
            .pipe(
                map((app: ApplicationsEnum) => Boolean(app)),
            );

        return combineLatest([isAuthenticated$, enabledRoles$])
            .pipe(
                map((keys: Array<boolean>) => keys.every(Boolean)),
            );
    }
}
